// Клиентские инструменты раздела Tools: разбор ссылки SharePoint Online
// (/tools/sharepoint-url), конвертер идентификаторов Microsoft Graph
// (/tools/graph-ids), декодер токенов Entra ID (/tools/token-decoder) и
// проверка путей перед переносом в SharePoint (/tools/sharepoint-path-checker).
// Всё считается в браузере — введённое никуда не уходит,
// поэтому страницы открыты всем и лежат в output cache.
//
// Разметка: <form data-tool="ИМЯ"> с полями по name и <div data-tool-output>
// внутри формы (или следующий за ней). Подписи приходят из ресурсов портала
// в <script type="application/json" id="tool-labels">.
//
// Формат drive ID («b!» + Base64 трёх GUID: site, web, list в порядке байтов
// .NET Guid.ToByteArray) Microsoft не документирует; он восстановлен по
// наблюдаемым идентификаторам (см. techmikael.com, 2021). Формат shares/u!
// описан в документации Graph (encoding sharing URLs).

type Labels = Record<string, string>;

interface Row {
    label: string;
    value: string;
    /** Ссылка вместо текста. */
    href?: string;
    /** Многострочное значение (команды) — блоком <pre>. */
    block?: boolean;
}

interface Group {
    title?: string;
    rows: Row[];
}

const GRAPH = "https://graph.microsoft.com/v1.0";

const labels: Labels = (function (): Labels {
    const element = document.getElementById("tool-labels");

    try {
        return element ? (JSON.parse(element.textContent || "{}") as Labels) : {};
    } catch {
        return {};
    }
})();

function t(key: string, fallback: string): string {
    return labels[key] || fallback;
}

// --- Вывод ------------------------------------------------------------------

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function renderError(output: HTMLElement, message: string): void {
    output.innerHTML = '<div class="alert alert-danger" role="alert">' + escapeHtml(message) + "</div>";
}

function renderGroups(output: HTMLElement, groups: Group[]): void {
    const copyLabel = t("copy", "Copy");
    const parts: string[] = ['<div class="tool-result">'];

    groups.forEach(function (group, index) {
        if (!group.rows.length) {
            return;
        }

        if (group.title) {
            parts.push('<h3 class="tool-result-subtitle' + (index === 0 ? " mt-0" : "") + '">' + escapeHtml(group.title) + "</h3>");
        }

        parts.push('<dl class="tool-result-details">');

        group.rows.forEach(function (row) {
            parts.push("<dt>" + escapeHtml(row.label) + "</dt>");

            const copy = '<button type="button" class="tool-copy" data-copy="' + escapeHtml(row.value) + '" aria-label="' + escapeHtml(copyLabel) + '">' + escapeHtml(copyLabel) + "</button>";

            if (row.block) {
                parts.push('<dd class="tool-value-block"><pre><code>' + escapeHtml(row.value) + "</code></pre>" + copy + "</dd>");
            } else if (row.href) {
                parts.push('<dd><a href="' + escapeHtml(row.href) + '" target="_blank" rel="noopener">' + escapeHtml(row.value) + "</a>" + copy + "</dd>");
            } else {
                parts.push("<dd><code>" + escapeHtml(row.value) + "</code>" + copy + "</dd>");
            }
        });

        parts.push("</dl>");
    });

    parts.push("</div>");
    output.innerHTML = parts.join("");
}

function initCopyButtons(): void {
    const copiedLabel = t("copied", "Copied");
    const copyLabel = t("copy", "Copy");

    document.addEventListener("click", function (event) {
        const target = event.target;

        if (!(target instanceof Element)) {
            return;
        }

        const button = target.closest<HTMLButtonElement>("button[data-copy]");

        if (!button || !navigator.clipboard) {
            return;
        }

        navigator.clipboard.writeText(button.getAttribute("data-copy") || "").then(function () {
            button.textContent = copiedLabel;
            button.classList.add("is-copied");

            window.setTimeout(function () {
                button.textContent = copyLabel;
                button.classList.remove("is-copied");
            }, 2000);
        }, function () {
            // Буфер недоступен — кнопка ничего не меняет.
        });
    });
}

// --- Общие помощники --------------------------------------------------------

function bytesToBase64(bytes: Uint8Array): string {
    let binary = "";
    bytes.forEach(function (b) {
        binary += String.fromCharCode(b);
    });
    return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array | null {
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    } catch {
        return null;
    }
}

/** Base64 URL-safe без дополнения — так Graph кодирует ссылки (shares/u!). */
function toUrlSafe(base64: string): string {
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromUrlSafe(value: string): string {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - (base64.length % 4)) % 4;
    return base64 + "=".repeat(padding);
}

/** GUID → 16 байт в порядке .NET Guid.ToByteArray (первые три группы little-endian). */
function guidToBytes(value: string): Uint8Array | null {
    const hex = value.trim().replace(/[{}-]/g, "");

    if (!/^[0-9a-fA-F]{32}$/.test(hex)) {
        return null;
    }

    const straight = new Uint8Array(16);
    for (let i = 0; i < 16; i++) {
        straight[i] = parseInt(hex.substr(i * 2, 2), 16);
    }

    const order = [3, 2, 1, 0, 5, 4, 7, 6, 8, 9, 10, 11, 12, 13, 14, 15];
    const bytes = new Uint8Array(16);
    order.forEach(function (source, index) {
        bytes[index] = straight[source];
    });

    return bytes;
}

function bytesToGuid(bytes: Uint8Array): string {
    const order = [3, 2, 1, 0, 5, 4, 7, 6, 8, 9, 10, 11, 12, 13, 14, 15];
    let hex = "";
    order.forEach(function (source) {
        hex += ("0" + bytes[source].toString(16)).slice(-2);
    });
    return hex.substr(0, 8) + "-" + hex.substr(8, 4) + "-" + hex.substr(12, 4) + "-" + hex.substr(16, 4) + "-" + hex.substr(20, 12);
}

function encodeShareUrl(url: string): string {
    return "u!" + toUrlSafe(bytesToBase64(new TextEncoder().encode(url)));
}

function parseAbsoluteUrl(raw: string): URL | null {
    let text = raw.trim();

    if (!text) {
        return null;
    }

    if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) {
        text = "https://" + text;
    }

    try {
        const url = new URL(text);
        return url.protocol === "https:" || url.protocol === "http:" ? url : null;
    } catch {
        return null;
    }
}

/** Путь с пробелами и не-ASCII в процентном кодировании, но без двойного кодирования. */
function encodePath(path: string): string {
    return path
        .split("/")
        .map(function (segment) {
            return encodeURIComponent(segment).replace(/%2B/g, "+").replace(/'/g, "%27");
        })
        .join("/");
}

// --- Разбор ссылки SharePoint -----------------------------------------------

const SHARE_KINDS: Record<string, string> = {
    w: "Word",
    x: "Excel",
    p: "PowerPoint",
    b: "PDF",
    f: "Folder",
    i: "Image",
    v: "Video",
    o: "OneNote",
    t: "Text",
    u: "File",
    fl: "Loop",
    li: "List"
};

interface SharePointLink {
    tenant: string;
    host: string;
    rootHost: string;
    hostKind: string;
    managedPath: string;
    /** Путь семейства сайтов: "/sites/x" или "" для корневого сайта. */
    sitePath: string;
    library: string;
    folder: string;
    file: string;
    page: string;
    uniqueId: string;
    shareKind: string;
    shareScope: string;
    shareToken: string;
    web: boolean;
    download: boolean;
}

function analyzeSharePointUrl(raw: string): SharePointLink | null {
    const url = parseAbsoluteUrl(raw);

    if (!url) {
        return null;
    }

    const host = url.hostname.toLowerCase();
    const match = /^([a-z0-9][a-z0-9-]*?)(-my|-admin|-myfiles|-files|-public)?\.sharepoint\.(com|us|de|cn)$/.exec(host);

    if (!match) {
        return null;
    }

    const suffix = match[2] || "";
    const link: SharePointLink = {
        tenant: match[1],
        host: host,
        rootHost: match[1] + ".sharepoint." + match[3],
        hostKind: suffix === "-my" ? t("hostOneDrive", "OneDrive")
            : suffix === "-admin" ? t("hostAdmin", "SharePoint admin center")
                : suffix === "-files" || suffix === "-myfiles" ? t("hostFiles", "File host")
                    : t("hostSharePoint", "SharePoint site"),
        managedPath: "",
        sitePath: "",
        library: "",
        folder: "",
        file: "",
        page: "",
        uniqueId: "",
        shareKind: "",
        shareScope: "",
        shareToken: "",
        web: url.searchParams.get("web") === "1",
        download: url.searchParams.get("download") === "1"
    };

    let path: string;
    try {
        path = decodeURIComponent(url.pathname);
    } catch {
        path = url.pathname;
    }

    let segments = path.split("/").filter(function (s) { return s.length > 0; });

    // Короткие ссылки общего доступа: /:w:/r/sites/x/... (r — путь),
    // /:x:/s/SiteName/Token (s — сайт), /:f:/g/personal/... (g — организация).
    if (segments.length && /^:[a-z]{1,2}:$/.test(segments[0])) {
        const kind = segments[0].slice(1, -1);
        link.shareKind = SHARE_KINDS[kind] || kind;
        link.shareScope = segments[1] || "";
        segments = segments.slice(2);

        const first = (segments[0] || "").toLowerCase();

        if (first !== "sites" && first !== "teams" && first !== "personal" && link.shareScope !== "r") {
            // /:x:/s/marketing/EZ...: имя сайта и токен элемента.
            if (segments.length >= 1) {
                link.managedPath = link.shareScope === "p" || suffix === "-my" ? "personal" : "sites";
                link.sitePath = "/" + link.managedPath + "/" + segments[0];
                link.shareToken = segments[1] || "";
            }
            return link;
        }
    }

    const first = (segments[0] || "").toLowerCase();

    if (segments.length >= 2 && (first === "sites" || first === "teams" || first === "personal")) {
        link.managedPath = first;
        link.sitePath = "/" + segments[0] + "/" + segments[1];
        segments = segments.slice(2);
    }

    const lower = segments.map(function (s) { return s.toLowerCase(); });
    const params = url.searchParams;

    // Системные страницы: Doc.aspx (документ по sourcedoc), onedrive.aspx (id), прочие _layouts.
    const layoutsIndex = lower.indexOf("_layouts");

    if (layoutsIndex >= 0) {
        const pageName = lower[lower.length - 1];

        if (pageName === "doc.aspx" || pageName === "doc2.aspx" || pageName === "wopiframe.aspx" || pageName === "wopiframe2.aspx") {
            link.uniqueId = (params.get("sourcedoc") || "").replace(/[{}]/g, "").toLowerCase();
            link.file = params.get("file") || "";
            return link;
        }

        const id = params.get("id") || params.get("RootFolder");

        if (id) {
            applyServerRelative(link, id);
        } else {
            link.page = "/" + segments.join("/");
        }

        return link;
    }

    // Представление библиотеки: /Shared Documents/Forms/AllItems.aspx?id=... или RootFolder=...
    const formsIndex = lower.indexOf("forms");

    if (formsIndex > 0 && lower[lower.length - 1].endsWith(".aspx")) {
        link.library = segments.slice(0, formsIndex).join("/");
        const id = params.get("id") || params.get("RootFolder");

        if (id) {
            applyServerRelative(link, id);
        }

        return link;
    }

    // Список: /Lists/Name/AllItems.aspx
    if (first === "lists" && segments.length >= 2) {
        link.library = segments[0] + "/" + segments[1];
        const id = params.get("RootFolder");

        if (id) {
            applyServerRelative(link, id);
        }

        return link;
    }

    if (first === "sitepages") {
        link.page = "/" + segments.join("/");
        return link;
    }

    if (segments.length) {
        link.library = segments[0];
        const tail = segments.slice(1);

        if (tail.length) {
            const last = tail[tail.length - 1];

            if (/\.[a-z0-9]{1,8}$/i.test(last) && !/\.aspx$/i.test(last)) {
                link.file = last;
                link.folder = tail.slice(0, -1).join("/");
            } else if (/\.aspx$/i.test(last)) {
                link.page = "/" + segments.join("/");
                link.library = "";
            } else {
                link.folder = tail.join("/");
            }
        }
    }

    return link;
}

/** Параметр id / RootFolder — server-relative путь элемента: /sites/x/Library/Folder/file.docx. */
function applyServerRelative(link: SharePointLink, serverRelative: string): void {
    let value = serverRelative;

    try {
        value = decodeURIComponent(serverRelative);
    } catch {
        // уже декодирован
    }

    let relative = value;

    if (link.sitePath && value.toLowerCase().startsWith(link.sitePath.toLowerCase() + "/")) {
        relative = value.substring(link.sitePath.length + 1);
    } else {
        relative = value.replace(/^\/+/, "");
    }

    const parts = relative.split("/").filter(function (s) { return s.length > 0; });

    if (!parts.length) {
        return;
    }

    link.library = parts.length > 1 && parts[0].toLowerCase() === "lists" ? parts[0] + "/" + parts[1] : parts[0];
    const tail = parts.slice(link.library.indexOf("/") >= 0 ? 2 : 1);

    if (tail.length) {
        const last = tail[tail.length - 1];

        if (/\.[a-z0-9]{1,8}$/i.test(last)) {
            link.file = last;
            link.folder = tail.slice(0, -1).join("/");
        } else {
            link.folder = tail.join("/");
        }
    }
}

function sharePointGroups(link: SharePointLink, original: string): Group[] {
    const siteUrl = "https://" + link.host + link.sitePath;
    const structure: Row[] = [
        { label: t("tenant", "Tenant"), value: link.tenant },
        { label: t("host", "Host"), value: link.hostKind + " — " + link.host }
    ];

    structure.push({ label: t("siteCollection", "Site collection"), value: siteUrl, href: siteUrl });
    structure.push({ label: t("managedPath", "Managed path"), value: link.managedPath ? "/" + link.managedPath + "/" : t("rootSite", "root site") });

    if (link.shareKind) {
        structure.push({ label: t("shareLinkType", "Shared item type"), value: link.shareKind });
    }

    if (link.shareScope) {
        structure.push({ label: t("shareScope", "Link scope"), value: link.shareScope });
    }

    if (link.shareToken) {
        structure.push({ label: t("shareToken", "Share token"), value: link.shareToken });
    }

    if (link.library) {
        structure.push({ label: t("library", "Library or list"), value: link.library });
    }

    if (link.folder) {
        structure.push({ label: t("folder", "Folder"), value: link.folder });
    }

    if (link.file) {
        structure.push({ label: t("file", "File"), value: link.file });
    }

    if (link.page) {
        structure.push({ label: t("page", "Page"), value: link.page });
    }

    if (link.uniqueId) {
        structure.push({ label: t("uniqueId", "Unique ID"), value: link.uniqueId });
    }

    const flags: string[] = [];
    if (link.web) {
        flags.push(t("flagWeb", "open in browser (web=1)"));
    }
    if (link.download) {
        flags.push(t("flagDownload", "download (download=1)"));
    }
    if (flags.length) {
        structure.push({ label: t("options", "Options"), value: flags.join(", ") });
    }

    // Пути и ссылки.
    const links: Row[] = [{ label: t("siteUrl", "Site URL"), value: siteUrl, href: siteUrl }];
    let serverRelative = "";

    if (link.library) {
        const parts = [link.library];
        if (link.folder) {
            parts.push(link.folder);
        }
        if (link.file) {
            parts.push(link.file);
        }
        serverRelative = link.sitePath + "/" + parts.join("/");
    } else if (link.page) {
        serverRelative = link.sitePath + link.page;
    }

    if (serverRelative) {
        structure.push({ label: t("serverRelativeUrl", "Server-relative URL"), value: serverRelative });

        const direct = "https://" + link.host + encodePath(serverRelative);
        links.push({ label: t("directUrl", "Direct link"), value: direct, href: direct });

        if (link.file) {
            links.push({ label: t("openInBrowser", "Open in browser"), value: direct + "?web=1", href: direct + "?web=1" });
            links.push({ label: t("downloadUrl", "Download link"), value: direct + "?download=1", href: direct + "?download=1" });
        }

        if (link.library && (link.file || link.folder)) {
            const parentPath = link.sitePath + "/" + link.library + (link.folder && link.file ? "/" + link.folder : link.file ? "" : "/" + link.folder.split("/").slice(0, -1).join("/"));
            const parent = "https://" + link.host + encodePath(parentPath.replace(/\/$/, ""));
            links.push({ label: t("parentFolder", "Parent folder"), value: parent, href: parent });
        }

        if (link.library) {
            const root = link.library.toLowerCase().startsWith("lists/")
                ? "https://" + link.host + encodePath(link.sitePath + "/" + link.library) + "/AllItems.aspx"
                : "https://" + link.host + encodePath(link.sitePath + "/" + link.library) + "/Forms/AllItems.aspx";
            links.push({ label: t("libraryRoot", "Library root"), value: root, href: root });
        }
    }

    // Microsoft Graph.
    const graph: Row[] = [];
    const graphSite = link.sitePath
        ? GRAPH + "/sites/" + link.host + ":" + encodePath(link.sitePath)
        : GRAPH + "/sites/" + link.host;
    graph.push({ label: t("graphSite", "Site by path"), value: "GET " + graphSite, block: true });

    const shareTarget = serverRelative && (link.file || link.folder)
        ? "https://" + link.host + encodePath(serverRelative)
        : original.trim();
    graph.push({ label: t("graphShare", "Item by sharing URL"), value: "GET " + GRAPH + "/shares/" + encodeShareUrl(shareTarget) + "/driveItem", block: true });

    // PnP PowerShell.
    const pnp: string[] = ['Connect-PnPOnline -Url "' + siteUrl + '" -Interactive'];

    if (link.file && serverRelative) {
        pnp.push('Get-PnPFile -Url "' + serverRelative + '" -AsFile -Path . -FileName "' + link.file + '"');
    } else if (link.library && link.folder) {
        pnp.push('Get-PnPFolderItem -FolderSiteRelativeUrl "' + link.library + "/" + link.folder + '"');
    } else if (link.library) {
        pnp.push('Get-PnPList -Identity "' + link.library.replace(/^Lists\//i, "") + '"');
    }

    return [
        { title: t("structure", "Link structure"), rows: structure },
        { title: t("links", "Links"), rows: links },
        { title: t("graph", "Microsoft Graph"), rows: graph },
        { title: t("pnp", "PnP PowerShell"), rows: [{ label: "PowerShell", value: pnp.join("\n"), block: true }] }
    ];
}

// --- Конвертер идентификаторов Graph ----------------------------------------

function decodeDriveId(raw: string): Group[] | string {
    const value = raw.trim();

    if (!/^b!/i.test(value)) {
        return t("errorDriveId", "Not a valid drive ID.");
    }

    const bytes = base64ToBytes(fromUrlSafe(value.substring(2)));

    if (!bytes || bytes.length !== 48) {
        return t("errorDriveId", "Not a valid drive ID.");
    }

    const siteId = bytesToGuid(bytes.subarray(0, 16));
    const webId = bytesToGuid(bytes.subarray(16, 32));
    const listId = bytesToGuid(bytes.subarray(32, 48));

    return [
        {
            rows: [
                { label: t("siteCollectionId", "Site collection ID"), value: siteId },
                { label: t("webId", "Web ID"), value: webId },
                { label: t("listId", "List ID"), value: listId }
            ]
        },
        {
            title: t("graph", "Microsoft Graph"),
            rows: [
                { label: t("graphRequest", "Graph request"), value: "GET " + GRAPH + "/drives/" + value + "\nGET " + GRAPH + "/sites/" + siteId + "," + webId + "/lists/" + listId, block: true }
            ]
        }
    ];
}

function encodeDriveId(siteId: string, webId: string, listId: string): Group[] | string {
    const site = guidToBytes(siteId);
    const web = guidToBytes(webId);
    const list = guidToBytes(listId);

    if (!site || !web || !list) {
        return t("errorGuid", "Enter three valid GUIDs.");
    }

    const bytes = new Uint8Array(48);
    bytes.set(site, 0);
    bytes.set(web, 16);
    bytes.set(list, 32);

    const driveId = "b!" + toUrlSafe(bytesToBase64(bytes));

    return [
        {
            rows: [
                { label: t("driveId", "Drive ID"), value: driveId },
                { label: t("graphRequest", "Graph request"), value: "GET " + GRAPH + "/drives/" + driveId + "/root/children", block: true }
            ]
        }
    ];
}

function decodeSiteId(raw: string): Group[] | string {
    const parts = raw.trim().split(",").map(function (s) { return s.trim(); });

    if (parts.length !== 3 || !parts[0] || !guidToBytes(parts[1]) || !guidToBytes(parts[2])) {
        return t("errorSiteId", "Not a valid Graph site ID.");
    }

    const siteId = bytesToGuid(guidToBytes(parts[1])!);
    const webId = bytesToGuid(guidToBytes(parts[2])!);

    return [
        {
            rows: [
                { label: t("hostname", "Host name"), value: parts[0].toLowerCase() },
                { label: t("siteCollectionId", "Site collection ID"), value: siteId },
                { label: t("webId", "Web ID"), value: webId },
                { label: t("graphRequest", "Graph request"), value: "GET " + GRAPH + "/sites/" + parts[0].toLowerCase() + "," + siteId + "," + webId, block: true }
            ]
        }
    ];
}

function encodeSiteId(hostname: string, siteId: string, webId: string): Group[] | string {
    const host = hostname.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    const site = guidToBytes(siteId);
    const web = guidToBytes(webId);

    if (!host || !site || !web) {
        return t("errorSiteId", "Not a valid Graph site ID.");
    }

    const id = host + "," + bytesToGuid(site) + "," + bytesToGuid(web);

    return [
        {
            rows: [
                { label: t("graphSiteId", "Graph site ID"), value: id },
                { label: t("graphRequest", "Graph request"), value: "GET " + GRAPH + "/sites/" + id + "\nGET " + GRAPH + "/sites/" + id + "/drives", block: true }
            ]
        }
    ];
}

function encodeShare(raw: string): Group[] | string {
    const url = parseAbsoluteUrl(raw);

    if (!url) {
        return t("errorAbsoluteUrl", "Enter a valid https:// URL.");
    }

    const id = encodeShareUrl(url.toString());

    return [
        {
            rows: [
                { label: t("shareId", "Share ID"), value: id },
                { label: t("graphRequest", "Graph request"), value: "GET " + GRAPH + "/shares/" + id + "\nGET " + GRAPH + "/shares/" + id + "/driveItem", block: true }
            ]
        }
    ];
}

// --- Декодер токенов Entra ID -----------------------------------------------
// JWT — три части Base64URL через точку: заголовок, утверждения, подпись.
// Расшифровываются первые две; подпись не проверяется (для этого нужен ключ
// издателя, а цель инструмента — прочитать токен, а не довериться ему).
// Зашифрованный токен (JWE, пять частей) прочитать может только его аудитория.

/** Известные ресурсы: appId → имя (aud бывает GUID-ом или URI ресурса). */
const KNOWN_RESOURCES: Record<string, string> = {
    "00000003-0000-0000-c000-000000000000": "Microsoft Graph",
    "https://graph.microsoft.com": "Microsoft Graph",
    "00000003-0000-0ff1-ce00-000000000000": "SharePoint Online",
    "00000002-0000-0ff1-ce00-000000000000": "Exchange Online",
    "https://outlook.office365.com": "Exchange Online",
    "00000007-0000-0000-c000-000000000000": "Dataverse / Dynamics 365",
    "00000009-0000-0000-c000-000000000000": "Power BI Service",
    "https://analysis.windows.net/powerbi/api": "Power BI Service",
    "797f4846-ba00-4fd7-ba43-dac1f8f63013": "Azure Resource Manager",
    "https://management.azure.com": "Azure Resource Manager",
    "https://management.core.windows.net/": "Azure Resource Manager",
    "cfa8b339-82a2-471a-a3c9-0fc0be7a4093": "Azure Key Vault",
    "https://vault.azure.net": "Azure Key Vault",
    "e406a681-f3d4-42a8-90b6-c2b029497af1": "Azure Storage",
    "https://storage.azure.com": "Azure Storage",
    "00000002-0000-0000-c000-000000000000": "Azure AD Graph (retired)",
    "https://graph.windows.net": "Azure AD Graph (retired)"
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Язык страницы — первый сегмент пути (/en/tools/...). */
function pageLang(): string {
    return window.location.pathname.split("/")[1] || "en";
}

/** Ссылка на справочник App ID: им же расшифровываются роли каталога (wids). */
function appIdHref(id: string): string {
    return "/" + pageLang() + "/tools/app-id?q=" + encodeURIComponent(id);
}

function decodeJwtPart(part: string): Record<string, unknown> | null {
    const bytes = base64ToBytes(fromUrlSafe(part));

    if (!bytes) {
        return null;
    }

    try {
        const json = JSON.parse(new TextDecoder("utf-8").decode(bytes));
        return json && typeof json === "object" && !Array.isArray(json) ? json as Record<string, unknown> : null;
    } catch {
        return null;
    }
}

function claimText(value: unknown): string {
    if (value === undefined || value === null) {
        return "";
    }

    if (Array.isArray(value)) {
        return value.map(claimText).join(", ");
    }

    return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/** «2 d 3 h», «5 h 10 min», «4 min»: длительность для срока жизни токена. */
function formatDuration(seconds: number): string {
    const minutes = Math.max(1, Math.round(Math.abs(seconds) / 60));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const rest = minutes % 60;
    const parts: string[] = [];

    if (days) {
        parts.push(days + " " + t("unitDay", "d"));
    }

    if (hours) {
        parts.push(hours + " " + t("unitHour", "h"));
    }

    if (rest && !days) {
        parts.push(rest + " " + t("unitMinute", "min"));
    }

    return parts.join(" ");
}

function formatEpoch(value: unknown): string | null {
    if (typeof value !== "number" || !isFinite(value)) {
        return null;
    }

    const date = new Date(value * 1000);
    return date.toLocaleString(document.documentElement.lang || undefined, { dateStyle: "medium", timeStyle: "medium" })
        + " (" + date.toISOString().replace(".000Z", "Z") + ")";
}

function decodeToken(raw: string): Group[] | string {
    const value = raw.trim().replace(/^bearer\s+/i, "").replace(/\s+/g, "");
    const parts = value.split(".");

    if (parts.length === 5) {
        return t("errorTokenEncrypted", "This token is encrypted (JWE): only the service it was issued for can read it.");
    }

    if (parts.length !== 3) {
        return t("errorToken", "This doesn't look like a JWT: expected three Base64URL parts separated by dots.");
    }

    const header = decodeJwtPart(parts[0]);
    const claims = decodeJwtPart(parts[1]);

    if (!header || !claims) {
        return t("errorToken", "This doesn't look like a JWT: expected three Base64URL parts separated by dots.");
    }

    const c = function (name: string): string {
        return claimText(claims[name]);
    };

    // Тип: у ID-токена есть nonce/«idtyp=id» и нет scp/roles; у токена
    // приложения (client credentials) нет пользователя — только роли.
    const scopes = c("scp").split(" ").filter(function (s) { return s.length > 0; });
    const roles = Array.isArray(claims["roles"]) ? (claims["roles"] as unknown[]).map(claimText) : [];
    const wids = Array.isArray(claims["wids"]) ? (claims["wids"] as unknown[]).map(claimText) : [];
    const appOnly = c("idtyp") === "app" || (!scopes.length && roles.length > 0 && !c("upn") && !c("preferred_username"));
    const isIdToken = c("idtyp") === "id" || (!scopes.length && !roles.length && (c("nonce") !== "" || c("aud") === c("azp") || c("aud") === c("appid")) && !appOnly);

    let type = isIdToken ? t("tokenTypeId", "ID token") : t("tokenTypeAccess", "Access token");
    if (appOnly) {
        type += " — " + t("tokenAppOnly", "app-only, no signed-in user");
    }

    const summary: Row[] = [{ label: t("tokenType", "Token type"), value: type }];

    if (c("ver")) {
        summary.push({ label: t("tokenVersion", "Token version"), value: c("ver") });
    }

    const aud = c("aud");
    if (aud) {
        const known = KNOWN_RESOURCES[aud.toLowerCase()] || KNOWN_RESOURCES[aud.replace(/\/$/, "")];
        summary.push({
            label: t("tokenAudience", "Audience (resource)"),
            value: known ? aud + " — " + known : aud,
            href: GUID_PATTERN.test(aud) && !known ? appIdHref(aud) : undefined
        });
    }

    const clientId = c("appid") || c("azp");
    if (clientId) {
        const name = c("app_displayname");
        summary.push({
            label: t("tokenClientApp", "Client application"),
            value: name ? clientId + " — " + name : clientId,
            href: GUID_PATTERN.test(clientId) ? appIdHref(clientId) : undefined
        });
    }

    if (c("tid")) {
        summary.push({ label: t("tenant", "Tenant"), value: c("tid") });
    }

    if (c("iss")) {
        summary.push({ label: t("tokenIssuer", "Issuer"), value: c("iss") });
    }

    // Срок жизни.
    const now = Date.now() / 1000;
    const lifetime: Row[] = [];
    const iat = formatEpoch(claims["iat"]);
    const nbf = formatEpoch(claims["nbf"]);
    const exp = formatEpoch(claims["exp"]);

    if (iat) {
        lifetime.push({ label: t("tokenIssuedAt", "Issued"), value: iat });
    }

    if (nbf) {
        lifetime.push({ label: t("tokenNotBefore", "Not before"), value: nbf });
    }

    if (exp) {
        const left = (claims["exp"] as number) - now;
        const status = left > 0
            ? t("tokenValidFor", "valid for another {0}").replace("{0}", formatDuration(left))
            : t("tokenExpired", "expired {0} ago").replace("{0}", formatDuration(left));
        lifetime.push({ label: t("tokenExpires", "Expires"), value: exp + " — " + status });
    }

    // Права.
    const permissions: Row[] = [];

    if (scopes.length) {
        permissions.push({ label: t("tokenScopes", "Delegated scopes (scp)"), value: scopes.join("\n"), block: true });
    }

    if (roles.length) {
        permissions.push({ label: t("tokenRoles", "Application roles (roles)"), value: roles.join("\n"), block: true });
    }

    wids.forEach(function (id, index) {
        permissions.push({ label: index === 0 ? t("tokenDirectoryRoles", "Directory roles (wids)") : "", value: id, href: appIdHref(id) });
    });

    // Пользователь.
    const identity: Row[] = [];
    const push = function (label: string, claim: string): void {
        if (c(claim)) {
            identity.push({ label: label, value: c(claim) });
        }
    };

    push(t("tokenName", "Name"), "name");
    push(t("tokenUser", "User"), c("upn") ? "upn" : c("preferred_username") ? "preferred_username" : "unique_name");
    push(t("tokenObjectId", "Object ID (oid)"), "oid");
    push(t("tokenAuthMethods", "Authentication methods (amr)"), "amr");
    push(t("tokenIpAddress", "IP address"), "ipaddr");

    // Заголовок: алгоритм и ключ подписи.
    const headerRows: Row[] = [];
    ["alg", "typ", "kid", "x5t"].forEach(function (name) {
        const text = claimText(header[name]);
        if (text) {
            headerRows.push({ label: name, value: text });
        }
    });

    return [
        { title: t("tokenSummary", "Summary"), rows: summary },
        { title: t("tokenLifetime", "Lifetime"), rows: lifetime },
        { title: t("tokenPermissions", "Permissions"), rows: permissions },
        { title: t("tokenIdentity", "Signed-in identity"), rows: identity },
        { title: t("tokenHeader", "Header"), rows: headerRows },
        { title: t("tokenClaims", "All claims (JSON)"), rows: [{ label: "JSON", value: JSON.stringify(claims, null, 2), block: true }] }
    ];
}

// --- Проверка путей перед переносом в SharePoint ----------------------------
// Правила — по статье Microsoft «Restrictions and limitations in OneDrive and
// SharePoint»: недопустимые символы " * : < > ? / \ |, пробелы в начале и конце
// имени, зарезервированные имена, «_vti_», префикс «~$», папка «forms» в корне
// библиотеки и 400 символов на весь декодированный путь вместе с адресом сайта.
// Предупреждения — то, что SharePoint примет, но синхронизация OneDrive и
// Windows обработают плохо.

const PATH_LIMIT = 400;
const PATH_LINES_LIMIT = 20000;
const INVALID_CHARS = /["*:<>?|]/g;
const RESERVED_NAMES = /^(\.lock|con|prn|aux|nul|com\d|lpt\d|desktop\.ini)$/i;
const NOT_SYNCED = /(\.tmp|\.ds_store|^thumbs\.db|^ehthumbs\.db)$/i;

interface PathIssue {
    error: boolean;
    text: string;
}

interface PathReport {
    path: string;
    length: number;
    issues: PathIssue[];
    fixed: string;
}

/** Путь назначения из адреса библиотеки: декодированный, без хоста и слэшей по краям. */
function destinationPath(raw: string): string | null {
    const value = raw.trim();

    if (!value) {
        return "";
    }

    const url = parseAbsoluteUrl(value);

    if (!url) {
        return null;
    }

    let path = url.pathname;

    try {
        path = decodeURIComponent(path);
    } catch {
        // Битая %-последовательность — считаем путь как есть.
    }

    return path.replace(/^\/+|\/+$/g, "");
}

function fixSegment(segment: string): string {
    let name = segment
        .replace(/[\u0000-\u001f]/g, "")
        .replace(INVALID_CHARS, "_")
        .replace(/_vti_/gi, "_vti-")
        .trim()
        .replace(/\.+$/, "");

    if (name.indexOf("~$") === 0) {
        name = "_" + name.substring(2);
    }

    if (RESERVED_NAMES.test(name)) {
        name = name + "_";
    }

    return name || "_";
}

function checkPath(line: string, prefix: string, destination: string): PathReport | null {
    let relative = line.replace(/\r/g, "");

    if (!relative.trim()) {
        return null;
    }

    // Префикс источника снимается без учёта регистра и вида слэшей.
    const normalized = relative.replace(/\\/g, "/");
    const normalizedPrefix = prefix.trim().replace(/\\/g, "/").replace(/\/+$/, "");

    if (normalizedPrefix && normalized.toLowerCase().indexOf(normalizedPrefix.toLowerCase()) === 0) {
        relative = normalized.substring(normalizedPrefix.length);
    } else {
        // Без префикса — убираем букву диска и \\сервер\шара, они в SharePoint не переносятся.
        relative = normalized.replace(/^[a-z]:\//i, "").replace(/^\/\/[^/]+\/[^/]+/, "");
    }

    const segments = relative.split("/").filter(function (s) { return s.length > 0; });

    if (!segments.length) {
        return null;
    }

    const issues: PathIssue[] = [];
    const add = function (error: boolean, text: string): void {
        issues.push({ error: error, text: text });
    };

    segments.forEach(function (segment, index) {
        const isLast = index === segments.length - 1;
        const invalid = segment.match(INVALID_CHARS);

        if (invalid) {
            add(true, t("issueInvalidChars", "Characters not allowed: {0}").replace("{0}", invalid.filter(function (ch, i, all) { return all.indexOf(ch) === i; }).join(" ")) + " — " + segment);
        }

        if (/[\u0000-\u001f]/.test(segment)) {
            add(true, t("issueControlChars", "Contains control characters") + " — " + segment);
        }

        if (/^\s/.test(segment)) {
            add(true, t("issueLeadingSpace", "Starts with a space") + " — \"" + segment + "\"");
        }

        if (/\s$/.test(segment)) {
            add(true, t("issueTrailingSpace", "Ends with a space") + " — \"" + segment + "\"");
        }

        if (RESERVED_NAMES.test(segment.trim())) {
            add(true, t("issueReservedName", "Reserved name: {0}").replace("{0}", segment.trim()));
        }

        if (/_vti_/i.test(segment)) {
            add(true, t("issueVti", "Names can't contain _vti_") + " — " + segment);
        }

        if (segment.indexOf("~$") === 0) {
            add(true, t("issueTildeDollar", "Names can't start with ~$ (Office lock files)") + " — " + segment);
        }

        if (index === 0 && !isLast && destination.split("/").length <= 3 && segment.toLowerCase() === "forms") {
            add(true, t("issueFormsFolder", "A folder named \"forms\" isn't allowed at the root of a library"));
        }

        if (/\.$/.test(segment.trim())) {
            add(false, t("issueTrailingPeriod", "Ends with a period: Windows and OneDrive sync may not handle it") + " — " + segment);
        }

        if (isLast && NOT_SYNCED.test(segment)) {
            add(false, t("issueNotSynced", "This file is usually skipped by OneDrive sync") + " — " + segment);
        }
    });

    const fullPath = (destination ? destination + "/" : "") + segments.join("/");

    if (fullPath.length > PATH_LIMIT) {
        add(true, t("issuePathTooLong", "Path is {0} characters, the limit is 400").replace("{0}", String(fullPath.length)));
    }

    return {
        path: segments.join("/"),
        length: fullPath.length,
        issues: issues,
        fixed: segments.map(fixSegment).join("/")
    };
}

function renderPathCheck(output: HTMLElement, destinationRaw: string, prefix: string, text: string): void {
    const destination = destinationPath(destinationRaw);

    if (destination === null) {
        renderError(output, t("errorAbsoluteUrl", "Enter a valid https:// URL."));
        return;
    }

    let lines = text.split("\n");
    const truncated = lines.length > PATH_LINES_LIMIT;

    if (truncated) {
        lines = lines.slice(0, PATH_LINES_LIMIT);
    }

    const reports = lines
        .map(function (line) { return checkPath(line, prefix, destination); })
        .filter(function (r): r is PathReport { return r !== null; });

    if (!reports.length) {
        renderError(output, t("errorPathsEmpty", "Paste at least one path."));
        return;
    }

    const withErrors = reports.filter(function (r) { return r.issues.some(function (i) { return i.error; }); });
    const withWarnings = reports.filter(function (r) { return r.issues.length > 0 && !r.issues.some(function (i) { return i.error; }); });
    const longest = reports.reduce(function (a, b) { return b.length > a.length ? b : a; });
    const problems = withErrors.concat(withWarnings);

    const parts: string[] = ['<div class="tool-result">'];

    if (truncated) {
        parts.push('<div class="alert alert-warning" role="alert">' + escapeHtml(t("pathsTruncated", "Only the first {0} lines were checked.").replace("{0}", String(PATH_LINES_LIMIT))) + "</div>");
    }

    parts.push('<dl class="tool-result-details">');
    parts.push("<dt>" + escapeHtml(t("pathsChecked", "Paths checked")) + "</dt><dd><code>" + reports.length + "</code></dd>");
    parts.push("<dt>" + escapeHtml(t("pathsWithErrors", "With errors")) + "</dt><dd><code>" + withErrors.length + "</code></dd>");
    parts.push("<dt>" + escapeHtml(t("pathsWithWarnings", "With warnings")) + "</dt><dd><code>" + withWarnings.length + "</code></dd>");
    parts.push("<dt>" + escapeHtml(t("pathsLongest", "Longest path")) + "</dt><dd><code>" + longest.length + " / " + PATH_LIMIT + "</code></dd>");
    parts.push("</dl>");

    if (!problems.length) {
        parts.push('<div class="alert alert-success mt-3 mb-0" role="status">' + escapeHtml(t("pathsAllGood", "No problems found: all paths can be uploaded as they are.")) + "</div>");
        parts.push("</div>");
        output.innerHTML = parts.join("");
        return;
    }

    // Список проблемных путей: первые 500 на экране, полный список — в блоке
    // переименования (оригинал и предложенное имя через табуляцию, для Excel или скрипта).
    const shown = problems.slice(0, 500);

    parts.push('<ul class="path-report">');
    shown.forEach(function (report) {
        const hasError = report.issues.some(function (i) { return i.error; });
        parts.push('<li class="' + (hasError ? "is-error" : "is-warning") + '"><code class="path-report-path">' + escapeHtml(report.path) + "</code><ul>");
        report.issues.forEach(function (issue) {
            parts.push('<li><span class="path-report-badge">' + escapeHtml(issue.error ? t("severityError", "Error") : t("severityWarning", "Warning")) + "</span> " + escapeHtml(issue.text) + "</li>");
        });
        parts.push("</ul></li>");
    });
    parts.push("</ul>");

    const renames = problems
        .filter(function (r) { return r.fixed !== r.path; })
        .map(function (r) { return r.path + "\t" + r.fixed; });

    if (renames.length) {
        const copyLabel = t("copy", "Copy");
        const value = renames.join("\n");
        parts.push('<h3 class="tool-result-subtitle">' + escapeHtml(t("pathsRenames", "Suggested renames (tab-separated)")) + "</h3>");
        parts.push('<div class="path-report-renames"><pre><code>' + escapeHtml(value) + '</code></pre><button type="button" class="tool-copy" data-copy="' + escapeHtml(value) + '" aria-label="' + escapeHtml(copyLabel) + '">' + escapeHtml(copyLabel) + "</button></div>");
    }

    parts.push("</div>");
    output.innerHTML = parts.join("");
}

// --- Привязка форм ----------------------------------------------------------

function field(form: HTMLFormElement, name: string): string {
    const input = form.elements.namedItem(name);
    return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement ? input.value : "";
}

function outputOf(form: HTMLFormElement): HTMLElement | null {
    return form.querySelector<HTMLElement>("[data-tool-output]")
        || (form.nextElementSibling instanceof HTMLElement && form.nextElementSibling.hasAttribute("data-tool-output") ? form.nextElementSibling : null);
}

function run(form: HTMLFormElement): void {
    const output = outputOf(form);

    if (!output) {
        return;
    }

    const tool = form.getAttribute("data-tool");
    let result: Group[] | string | null = null;

    switch (tool) {
        case "sharepoint-url": {
            const raw = field(form, "url");
            const link = analyzeSharePointUrl(raw);
            result = link ? sharePointGroups(link, raw) : t("errorUrl", "This is not a SharePoint Online link.");
            break;
        }
        case "drive-id":
            result = decodeDriveId(field(form, "driveId"));
            break;
        case "drive-id-encode":
            result = encodeDriveId(field(form, "siteId"), field(form, "webId"), field(form, "listId"));
            break;
        case "site-id":
            result = decodeSiteId(field(form, "siteId"));
            break;
        case "site-id-encode":
            result = encodeSiteId(field(form, "hostname"), field(form, "siteId"), field(form, "webId"));
            break;
        case "share-url":
            result = encodeShare(field(form, "url"));
            break;
        case "token":
            result = decodeToken(field(form, "token"));
            break;
        case "path-check":
            // Отчёт — список с подсветкой, а не пары «подпись — значение»: рисуется сам.
            renderPathCheck(output, field(form, "destination"), field(form, "prefix"), field(form, "paths"));
            return;
    }

    if (result === null) {
        return;
    }

    if (typeof result === "string") {
        renderError(output, result);
    } else {
        renderGroups(output, result);
    }
}

function initTools(): void {
    document.querySelectorAll<HTMLFormElement>("form[data-tool]").forEach(function (form) {
        form.addEventListener("submit", function (event) {
            event.preventDefault();
            run(form);
        });
    });

    initCopyButtons();

    // Ссылка в параметре url: /tools/sharepoint-url?url=... — разбираем сразу.
    const preset = new URLSearchParams(window.location.search).get("url");
    const urlForm = document.querySelector<HTMLFormElement>('form[data-tool="sharepoint-url"]');

    if (preset && urlForm) {
        const input = urlForm.elements.namedItem("url");
        if (input instanceof HTMLInputElement) {
            input.value = preset;
            run(urlForm);
        }
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initTools);
} else {
    initTools();
}
